import { Injectable, computed, inject, signal } from '@angular/core';

import { UserModel } from '@portal/app/shared/models/userModel';
import { AuthService } from '@portal/app/shared/services/auth.service';
import { StorageService } from '@portal/app/shared/services/storage.service';

import {
  BrandKey,
  RecentBrandsMap,
  pushRecent,
  readRecents
} from '@portal/app/dashboard/navbar/navbar.utils';

const RECENT_BRANDS_KEY = 'recentBrands';

/**
 * Owns the per-user most-recently-used brand list and its persistence.
 * Pure MRU logic lives in navbar.utils (pushRecent/readRecents); this service
 * is the stateful glue between that, signals, and storage.
 */
@Injectable({ providedIn: 'root' })
export class RecentBrandsService {
  private readonly storageService = inject(StorageService);
  private readonly userId =
    (inject(AuthService).getUser() as UserModel | null)?.getUsername?.() ?? '';

  private readonly recents = signal<RecentBrandsMap>(this.read());

  /** MRU brand keys for the current user (most-recent-first, max 3). */
  readonly keys = computed<BrandKey[]>(() =>
    readRecents(this.recents(), this.userId)
  );

  record(brand: BrandKey): void {
    if (!this.userId) {
      return;
    }
    const next = pushRecent(this.recents(), this.userId, brand);
    this.recents.set(next);
    this.storageService.set(RECENT_BRANDS_KEY, JSON.stringify(next));
  }

  private read(): RecentBrandsMap {
    const raw = this.storageService.get(
      RECENT_BRANDS_KEY
    ) as unknown as RecentBrandsMap | null;
    return raw && typeof raw === 'object' ? raw : {};
  }
}
